import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Beaker, Leaf, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { References } from "@/components/References";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";

const URL = "https://truwe.in/blog/gut-liver-axis";
const TITLE = "The Gut-Liver Axis: How Your Gut Controls Your Liver Health | TruWe";

export const Route = createFileRoute("/blog/gut-liver-axis")({
  component: GutLiverArticle,
  head: () => buildBlogHead({
    slug: "gut-liver-axis",
    title: "The Gut-Liver Axis: How Your Gut Controls Your Liver Health | TruWe",
    description: "Your liver processes 70% of its blood from the gut. Here's how gut dysbiosis drives fatty liver, inflammation & toxin overload, and the evidence-based protocol to fix it.",
    ogTitle: "The Gut-Liver Axis: How Your Gut Controls Your Liver Health",
    ogDescription: "Your liver processes 70% of its blood from the gut. Here's how gut dysbiosis drives fatty liver, inflammation & toxin overload, and the evidence-based protocol to fix it.",
    headline: "The Gut-Liver Axis: How Your Gut Controls Your Liver Health",
    breadcrumbName: "The Gut-Liver Axis: How Your Gut Controls Your Liver Health",
    datePublished: "2026-06-08",
    dateModified: "2026-07-25",
    faqs: [
              {
                q: "What is the gut-liver axis?",
                a: "The gut-liver axis is the bidirectional relationship between your intestinal microbiome and your liver. Roughly 70% of the blood entering the liver comes from the gut via the portal vein. A disrupted gut sends bacterial endotoxins (LPS), inflammatory metabolites and undigested food particles straight to the liver, driving NAFLD, insulin resistance and systemic inflammation.",
              },
              {
                q: "Can a leaky gut cause fatty liver?",
                a: "Yes. Leaky gut increases intestinal permeability, allowing bacterial LPS to cross into the portal circulation. The liver detects LPS and mounts an inflammatory response. Over time this creates a low-grade inflammatory state that promotes hepatic fat accumulation and NAFLD progression.",
              },
              {
                q: "Which probiotic strains help the gut-liver axis?",
                a: "Multi-strain blends of Lactobacillus and Bifidobacterium species have the strongest evidence for lowering portal LPS, reducing liver enzymes (ALT/AST), and improving NAFLD in human trials. The key is strain diversity and adequate CFU count (10 billion or more).",
              },
              {
                q: "How long does it take to repair the gut-liver axis?",
                a: "Most people notice reduced bloating, steadier energy and better bowel regularity within 2-3 weeks. Liver enzyme improvements and measurable reductions in hepatic fat typically appear after 12 weeks of consistent intervention with probiotics, diet changes and targeted liver cofactors.",
              },
            ],
  }),
});

function GutLiverArticle() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Liver & Gut
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            The Gut-Liver Axis: How Your Gut Controls Your Liver Health
          </h1>
          <BlogByline date="2026-06-08" readingTime="11 min read" />
          <div className="mt-4"><ReadWithLlm title={TITLE} url={URL} kind="article" /></div>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Your liver does not work in isolation. Roughly 70% of the blood that enters
            it comes straight from the intestine via the portal vein. When the gut is
            inflamed, leaky or dysbiotic, the liver becomes the first organ to absorb the
            damage. Understanding this axis is the single most important step in fixing
            fatty liver, sluggish detox and chronic inflammation.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            The portal vein: the hidden highway
          </h2>
          <p className="mt-3 text-base text-foreground/85">
            After you eat, nutrients and bacterial metabolites are absorbed into the
            portal circulation and delivered to the liver for filtration, processing
            and storage. This is efficient, but it also means the liver is the first
            checkpoint for everything that goes wrong in the gut.
          </p>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Bacterial endotoxin (LPS).</strong> Gram-negative gut bacteria
              shed lipopolysaccharide. In a healthy gut, the intestinal barrier keeps
              most of it out. In a leaky gut, LPS floods the portal vein and triggers
              TLR4 receptors on liver cells, sparking NF-κB-driven inflammation.
            </li>
            <li>
              <strong>Ethanol from bacterial fermentation.</strong> Gut dysbiosis
              produces endogenous alcohol that the liver must clear, adding metabolic
              load even in non-drinkers.
            </li>
            <li>
              <strong>Bile acid disruption.</strong> The gut microbiome metabolises
              primary bile acids into secondary forms. An imbalanced microbiome
              produces toxic bile acid profiles that damage hepatocytes and promote
              cholestasis.
            </li>
            <li>
              <strong>Short-chain fatty acids (SCFAs).</strong> On the positive side,
              beneficial bacteria produce butyrate, propionate and acetate, which
              reduce liver inflammation, improve insulin sensitivity and strengthen
              the gut barrier. Low SCFA output is strongly associated with NAFLD.
            </li>
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            How dysbiosis drives fatty liver
          </h2>
          <p className="mt-3 text-base text-foreground/85">
            Non-alcoholic fatty liver disease (NAFLD) was once considered a liver
            problem. We now know it starts in the gut. The sequence looks like this:
          </p>
          <ol className="mt-4 list-decimal space-y-3 pl-5 text-base text-foreground/85">
            <li>
              <strong>Dietary insult.</strong> High fructose, refined carbs and seed
              oils shift the microbiome toward LPS-producing species and reduce
              butyrate producers.
            </li>
            <li>
              <strong>Intestinal permeability rises.</strong> Tight-junction proteins
              (occludin, claudin, zonulin) break down. The gut wall becomes leaky.
            </li>
            <li>
              <strong>LPS enters the portal vein.</strong> The liver's Kupffer cells
              detect LPS and release TNF-α, IL-6 and IL-1β, creating a low-grade
              inflammatory fire.
            </li>
            <li>
              <strong>Insulin resistance follows.</strong> Inflammation blocks insulin
              signalling in hepatocytes. The liver compensates by converting glucose
              to fat (de novo lipogenesis).
            </li>
            <li>
              <strong>Fat accumulates.</strong> Triglycerides pile up in hepatocytes.
              ALT and AST rise. NAFLD is now established, and the gut is still
              feeding it.
            </li>
          </ol>
          <p className="mt-4 text-base text-foreground/85">
            This is why treating fatty liver with liver-only supplements often fails.
            If the gut is still leaking LPS, the liver never gets a break.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            Probiotics: the upstream fix
          </h2>
          <p className="mt-3 text-base text-foreground/85">
            Multiple randomised controlled trials have shown that multi-strain
            probiotics lower liver enzymes, reduce hepatic fat and improve insulin
            sensitivity in NAFLD patients, independent of weight loss. The mechanism
            is not "detox." It is gut restoration.
          </p>
          <div className="mt-5 space-y-4">
            {[
              {
                name: "Lactobacillus & Bifidobacterium blends",
                use: "Best-studied for NAFLD",
                note: "Meta-analyses of 10+ RCTs show significant reductions in ALT, AST, GGT and inflammatory markers (CRP, TNF-α) with multi-strain blends at 10 billion CFU or higher.",
              },
              {
                name: "Butyrate producers",
                use: "Barrier repair",
                note: "Faecalibacterium prausnitzii and Roseburia species generate butyrate, the primary fuel for colonocytes and a direct tight-junction strengthener. Low levels correlate with leaky gut and NAFLD severity.",
              },
              {
                name: "LPS reducers",
                use: "Endotoxin load",
                note: "Certain Lactobacillus strains bind and neutralise LPS in the intestinal lumen before it reaches the portal vein. This is measurable in blood LPS assays.",
              },
            ].map((row) => (
              <div key={row.name} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <Beaker className="h-4 w-4 text-brand" />
                  <h3 className="font-display text-lg font-semibold text-brand-deep">{row.name}</h3>
                </div>
                <p className="mt-1 text-sm font-medium text-brand">{row.use}</p>
                <p className="mt-2 text-sm text-foreground/80">{row.note}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            The liver side: why you still need hepatic support
          </h2>
          <p className="mt-3 text-base text-foreground/85">
            Fixing the gut stops the fire from being fed. But the liver still needs
            cofactors to repair existing damage, process the daily toxin load and
            regenerate hepatocytes. The research-backed liver stack includes:
          </p>
          <div className="mt-5 space-y-4">
            {[
              {
                name: "Silymarin (milk thistle)",
                use: "Hepatocyte membrane stabiliser",
                note: "Liposomal silymarin shows 4-10× absorption over standard milk thistle. Multiple RCTs demonstrate ALT/AST reduction and antioxidant enzyme upregulation in NAFLD.",
              },
              {
                name: "N-acetylcysteine (NAC)",
                use: "Glutathione precursor",
                note: "Glutathione is the liver's master antioxidant and the primary conjugating agent in Phase II detox. NAC at 600 mg reliably raises hepatic glutathione stores.",
              },
              {
                name: "Alpha-lipoic acid (ALA)",
                use: "Mitochondrial antioxidant, insulin sensitiser",
                note: "Liposomal R-ALA recycles vitamins C and E, reduces oxidative stress in hepatocytes, and improves insulin sensitivity in fatty liver studies.",
              },
              {
                name: "Curcumin",
                use: "NF-κB inhibition",
                note: "Curcumin blocks the NF-κB pathway that LPS activates in the liver. Liposomal delivery is essential, standard curcumin is under 1% bioavailable.",
              },
            ].map((row) => (
              <div key={row.name} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <Beaker className="h-4 w-4 text-brand" />
                  <h3 className="font-display text-lg font-semibold text-brand-deep">{row.name}</h3>
                </div>
                <p className="mt-1 text-sm font-medium text-brand">{row.use}</p>
                <p className="mt-2 text-sm text-foreground/80">{row.note}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            Diet rules that actually matter
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Eliminate liquid fructose.</strong> Soft drinks, juices and
              sweetened lattes are the fastest way to shift your microbiome toward
              LPS-producing species and overload hepatic de novo lipogenesis. Whole
              fruit is fine, the fibre slows absorption.
            </li>
            <li>
              <strong>Prioritise fermentable fibre.</strong> Oats, lentils, beans,
              green bananas and cooked-and-cooled potatoes feed butyrate producers.
              Aim for 30-40 g of fibre daily.
            </li>
            <li>
              <strong>Eat polyphenol-rich foods.</strong> Extra-virgin olive oil,
              berries, green tea and dark chocolate selectively nourish beneficial
              bacteria while inhibiting pathogenic overgrowth.
            </li>
            <li>
              <strong>Fix meal timing.</strong> A 12-14 hour overnight fast gives the
              gut microbiome time to reset and reduces portal endotoxin load. Early
              time-restricted eating (finish by 7 PM) shows particular benefit for
              liver enzymes.
            </li>
            <li>
              <strong>Fermented foods, not just probiotics.</strong> Kimchi, kefir,
              sauerkraut and miso introduce live cultures and bacterial metabolites
              that stabilise the gut ecosystem faster than supplements alone.
            </li>
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            The testing roadmap
          </h2>
          <p className="mt-3 text-base text-foreground/85">
            If you suspect gut-liver axis dysfunction, ask your doctor for:
          </p>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Liver function panel:</strong> ALT, AST, GGT, alkaline
              phosphatase, bilirubin.
            </li>
            <li>
              <strong>Metabolic markers:</strong> Fasting insulin, HOMA-IR, HbA1c,
              lipid panel (triglycerides/HDL ratio is a strong NAFLD predictor).
            </li>
            <li>
              <strong>Gut assessment:</strong> Comprehensive stool analysis
              (butyrate, calprotectin, zonulin), SIBO breath test if bloating is
              prominent.
            </li>
            <li>
              <strong>Imaging:</strong> FibroScan or liver ultrasound with elastography
              to quantify hepatic fat and stiffness.
            </li>
          </ul>
          <p className="mt-4 text-base text-foreground/85">
            Retest liver enzymes at 12 weeks after starting a gut-liver protocol.
            A 20-40% drop in ALT is a realistic and meaningful target.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-14 rounded-3xl border border-brand/15 bg-gradient-to-br from-brand/5 via-background to-brand-deep/5 p-8 md:p-10">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand">
              <Sparkles className="h-4 w-4" /> The TruWe formulation
            </div>
            <h2 className="mt-3 font-display text-2xl font-bold text-brand-deep md:text-3xl">
              Liver Biome Plus, gut and liver in one capsule
            </h2>
            <p className="mt-3 text-base text-foreground/85">
              Liver Biome Plus is built around the gut-liver axis, not around either organ
              in isolation. It combines <strong>liposomal silymarin, NAC, liposomal
              curcumin, liposomal alpha-lipoic acid and a 10-billion CFU multi-strain
              probiotic</strong> the full upstream (gut) and downstream (liver)
              protocol in a single daily dose. No juice cleanses. No senna. No
              theatre.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                { icon: Leaf, label: "Liposomal delivery" },
                { icon: ShieldCheck, label: "Third-party tested" },
                { icon: Beaker, label: "USFDA / GMP / FSSAI facility" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 rounded-xl bg-background/70 px-3 py-3 text-sm text-foreground/85 ring-1 ring-border"
                >
                  <Icon className="h-4 w-4 text-brand" />
                  {label}
                </div>
              ))}
            </div>
            <Link
              to="/products/$handle"
              params={{ handle: "liver-biome-plus" }}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-luxury transition hover:bg-brand-deep"
            >
              Explore Liver Biome Plus <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">FAQs</h2>
          <div className="mt-4 space-y-5 text-base text-foreground/85">
            <div>
              <h3 className="font-semibold text-brand-deep">How do I know if my gut is affecting my liver?</h3>
              <p className="mt-1">
                Clues include: chronic bloating, irregular bowel movements, morning
                fatigue, dull skin, mid-section weight that won't shift, and mildly
                elevated ALT/AST on bloodwork. A comprehensive stool test and liver
                enzyme panel can confirm the connection.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-brand-deep">Is a probiotic alone enough?</h3>
              <p className="mt-1">
                Probiotics address the upstream (gut) side of the axis, but the liver
                still needs cofactors for Phase I and Phase II detox, antioxidant
                defence and hepatocyte repair. The best results come from combining
                probiotics with silymarin, NAC, ALA and curcumin, plus dietary change.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-brand-deep">Can I reverse fatty liver with diet and supplements?</h3>
              <p className="mt-1">
                In most cases, yes. NAFLD is reversible in its early stages. RCTs show
                that a combination of reduced fructose, increased fibre, daily exercise
                and targeted supplementation can reduce hepatic fat by 30-50% in 6-12
                months.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-brand-deep">Should I take Liver Biome Plus with food?</h3>
              <p className="mt-1">
                Yes, morning, with your first meal. Dietary fat improves liposomal
                absorption, and morning timing aligns with the body's natural
                detoxification rhythm.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <ClinicalProductPitch cluster="liver-gut" />
          <ClusterLinks cluster="liver-gut" />
          <AuthorityFlow cluster="liver-gut" />
          <References slug="gut-liver-axis" />
          <RelatedReading articles={relatedArticles("gut-liver-axis")} />
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-xs text-muted-foreground">
            This article is for general education only and is not a substitute for medical advice.
            Consult a qualified healthcare provider before beginning any new supplement protocol,
            especially if you have diagnosed liver disease or are on prescription medication.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
